import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Calendar, Clock, Plus, User, Phone, Mail, CheckCircle, XCircle,
  Video, ChevronLeft, ChevronRight, MoreHorizontal, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, isSameDay, parseISO, isToday } from 'date-fns';

import { API_URL } from '@/config/api';

const statusConfig = {
  scheduled: { color: 'bg-blue-100 text-blue-700', label: 'Scheduled' },
  confirmed: { color: 'bg-emerald-100 text-emerald-700', label: 'Confirmed' },
  completed: { color: 'bg-slate-100 text-slate-700', label: 'Completed' },
  no_show: { color: 'bg-red-100 text-red-700', label: 'No Show' },
  cancelled: { color: 'bg-slate-100 text-slate-500', label: 'Cancelled' },
  rescheduled: { color: 'bg-amber-100 text-amber-700', label: 'Rescheduled' }
};

const outcomeConfig = {
  closed: { color: 'bg-emerald-100 text-emerald-700', label: 'Closed Won' },
  follow_up: { color: 'bg-amber-100 text-amber-700', label: 'Follow-up Needed' },
  not_interested: { color: 'bg-red-100 text-red-700', label: 'Not Interested' }
};

const AppointmentsPage = () => {
  const { user, selectedBusiness } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isOutcomeModalOpen, setIsOutcomeModalOpen] = useState(false);
  const [outcomeData, setOutcomeData] = useState({ outcome: '', outcome_notes: '' });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [apptRes, calRes, todayRes] = await Promise.all([
        fetch(`${API_URL}/api/appointments`, { headers }),
        fetch(`${API_URL}/api/calendars`, { headers }),
        fetch(`${API_URL}/api/appointments/today`, { headers })
      ]);

      if (apptRes.ok) setAppointments(await apptRes.json());
      if (calRes.ok) setCalendars(await calRes.json());
      if (todayRes.ok) setTodaysAppointments(await todayRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load appointments');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/appointments/${appointmentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Appointment updated');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const handleOutcomeSubmit = async () => {
    if (!selectedAppointment || !outcomeData.outcome) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/appointments/${selectedAppointment.appointment_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'completed',
          outcome: outcomeData.outcome,
          outcome_notes: outcomeData.outcome_notes
        })
      });

      if (response.ok) {
        toast.success('Outcome recorded!');
        setIsOutcomeModalOpen(false);
        setSelectedAppointment(null);
        setOutcomeData({ outcome: '', outcome_notes: '' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to record outcome');
    }
  };

  const getAppointmentsForDate = (date) => {
    return appointments.filter(appt => {
      const apptDate = parseISO(appt.scheduled_at);
      return isSameDay(apptDate, date);
    });
  };

  // Week view
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Appointments</h1>
          <p className="text-sm sm:text-base text-slate-500">Manage your scheduled meetings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSelectedDate(new Date())} className="h-10">
            Today
          </Button>
        </div>
      </div>

      {/* Today's Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{todaysAppointments.length}</p>
                <p className="text-xs text-slate-500">Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {todaysAppointments.filter(a => a.status === 'scheduled').length}
                </p>
                <p className="text-xs text-slate-500">Upcoming</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {appointments.filter(a => a.status === 'completed').length}
                </p>
                <p className="text-xs text-slate-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {appointments.filter(a => a.status === 'no_show').length}
                </p>
                <p className="text-xs text-slate-500">No Shows</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => setSelectedDate(addDays(selectedDate, -7))}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h3 className="text-lg font-semibold">
          {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
        </h3>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => setSelectedDate(addDays(selectedDate, 7))}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Week View */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {weekDays.map((day) => {
          const dayAppointments = getAppointmentsForDate(day);
          const isCurrentDay = isToday(day);
          
          return (
            <div 
              key={day.toISOString()}
              className={`min-h-[120px] sm:min-h-[150px] border rounded-lg p-2 ${
                isCurrentDay ? 'bg-cyan-50 border-cyan-200' : 'bg-white border-slate-200'
              }`}
            >
              <div className="text-center mb-2">
                <p className="text-xs text-slate-500">{format(day, 'EEE')}</p>
                <p className={`text-lg font-bold ${isCurrentDay ? 'text-cyan-600' : 'text-slate-900'}`}>
                  {format(day, 'd')}
                </p>
              </div>
              
              <div className="space-y-1">
                {dayAppointments.slice(0, 3).map((appt) => (
                  <button
                    key={appt.appointment_id}
                    onClick={() => {
                      setSelectedAppointment(appt);
                      setIsOutcomeModalOpen(true);
                    }}
                    className={`w-full text-left p-1.5 rounded text-xs truncate ${
                      statusConfig[appt.status]?.color || 'bg-slate-100'
                    }`}
                  >
                    <span className="font-medium">{format(parseISO(appt.scheduled_at), 'HH:mm')}</span>
                    <span className="hidden sm:inline ml-1">{appt.lead_name}</span>
                  </button>
                ))}
                {dayAppointments.length > 3 && (
                  <p className="text-xs text-center text-slate-500">
                    +{dayAppointments.length - 3} more
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Today's Appointments List */}
      {todaysAppointments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-cyan-500" />
              Today's Appointments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {todaysAppointments.map((appt) => (
                <div 
                  key={appt.appointment_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-900">
                        {format(parseISO(appt.scheduled_at), 'HH:mm')}
                      </p>
                      <p className="text-xs text-slate-500">{appt.duration_minutes} min</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{appt.lead_name}</p>
                      <p className="text-sm text-slate-500">{appt.calendar_name}</p>
                      <div className="flex gap-2 mt-1">
                        {appt.lead_phone && (
                          <a href={`tel:${appt.lead_phone}`} className="text-xs text-cyan-600 hover:underline">
                            <Phone className="w-3 h-3 inline mr-1" />{appt.lead_phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={statusConfig[appt.status]?.color}>
                      {statusConfig[appt.status]?.label}
                    </Badge>
                    {appt.status === 'scheduled' && (
                      <Select
                        value={appt.status}
                        onValueChange={(v) => handleStatusUpdate(appt.appointment_id, v)}
                      >
                        <SelectTrigger className="w-[130px] h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirmed">Confirm</SelectItem>
                          <SelectItem value="no_show">No Show</SelectItem>
                          <SelectItem value="cancelled">Cancel</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {appt.status === 'confirmed' && (
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedAppointment(appt);
                          setIsOutcomeModalOpen(true);
                        }}
                        className="bg-emerald-500 hover:bg-emerald-600 h-9"
                      >
                        Record Outcome
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Outcome Modal */}
      <Dialog open={isOutcomeModalOpen} onOpenChange={setIsOutcomeModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Appointment Outcome</DialogTitle>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedAppointment.lead_name}</p>
                <p className="text-sm text-slate-500">
                  {format(parseISO(selectedAppointment.scheduled_at), 'PPpp')}
                </p>
              </div>

              <div>
                <Label>Outcome</Label>
                <Select
                  value={outcomeData.outcome}
                  onValueChange={(v) => setOutcomeData({ ...outcomeData, outcome: v })}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select outcome..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="closed">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        Closed Won
                      </div>
                    </SelectItem>
                    <SelectItem value="follow_up">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        Follow-up Needed
                      </div>
                    </SelectItem>
                    <SelectItem value="not_interested">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        Not Interested
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  placeholder="Add notes about the appointment..."
                  className="mt-1.5"
                  value={outcomeData.outcome_notes}
                  onChange={(e) => setOutcomeData({ ...outcomeData, outcome_notes: e.target.value })}
                />
              </div>

              {outcomeData.outcome === 'follow_up' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    A follow-up callback will be automatically scheduled for 3 days from now.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOutcomeModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleOutcomeSubmit}
              disabled={!outcomeData.outcome}
              className="bg-cyan-500 hover:bg-cyan-600"
            >
              Save Outcome
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentsPage;
